import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError, waLink } from "../lib/api";
import { useSettings } from "../lib/useSettings";
import { WhatsAppIcon } from "./FloatingWhatsApp";

export const SERVICE_OPTIONS = [
  "Website Development",
  "AI Voice Agent",
  "WhatsApp AI Agent",
  "Website Chatbot",
  "Instagram AI Agent",
  "Facebook AI Agent",
  "CRM Integration",
  "Complete Automation Solution",
  "Other",
];

export const INDUSTRY_OPTIONS = [
  "Manufacturing & Industrial",
  "Hospitals & Clinics",
  "Retail & Wholesale",
  "Educational Institutions",
  "Real Estate",
  "Consultants & Professional Services",
  "Travel & Hospitality",
  "Recruitment & Job Consultancies",
  "Logistics & Distribution",
  "Small & Medium Enterprise",
  "Startup / Growing Business",
  "Other",
];

const BUDGETS = ["Under ₹50,000", "₹50,000 – ₹1,00,000", "₹1,00,000 – ₹2,50,000", "Above ₹2,50,000", "Not sure yet"];
const TIMELINES = ["Immediately", "Within 1 month", "1–3 months", "3+ months", "Just exploring"];

const inputCls =
  "w-full rounded-lg bg-slate-900/70 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "block text-xs font-medium text-slate-300 mb-1.5";

export default function EnquiryForm({ sourcePage = "contact", defaultServices = [], compact = false }) {
  const settings = useSettings();
  const [form, setForm] = useState({
    full_name: "", company_name: "", mobile: "", email: "", city: "",
    industry: "", services: defaultServices, website_url: "", budget: "",
    timeline: "", requirements: "", preferred_contact: "Phone", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setForm((f) => ({ ...f, services: defaultServices }));
  }, [defaultServices]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    set("services", form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name";
    if (form.company_name.trim().length < 2) e.company_name = "Please enter your company name";
    if (form.mobile.replace(/\D/g, "").length < 8) e.mobile = "Enter a valid mobile number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid business email";
    if (!form.services.length) e.services = "Select at least one service";
    if (form.requirements.trim().length < 10) e.requirements = "Tell us a little more (min 10 characters)";
    if (!form.consent) e.consent = "Consent is required to submit";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting || result) return;
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, source_page: sourcePage });
      setResult(data);
      toast.success(data.status === "duplicate" ? "Enquiry already received." : "Enquiry submitted successfully.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div data-testid="enquiry-success" className="glass-card rounded-2xl p-8 sm:p-10 text-center">
        <CheckCircle2 className="mx-auto text-green-400 mb-4" size={44} />
        <h3 className="font-display text-2xl font-bold text-slate-50">
          {result.status === "duplicate" ? "We already have your enquiry" : "Thank you — enquiry received"}
        </h3>
        <p className="mt-3 text-sm text-slate-400">
          Your enquiry reference is{" "}
          <span className="font-mono2 text-blue-400" data-testid="enquiry-reference">{result.reference}</span>.
          Our team will review your requirements and reach out through your preferred contact method.
        </p>
        <a
          data-testid="enquiry-continue-whatsapp"
          href={waLink(settings, `Hello RIDGELINE DIGI PARTNER, I just submitted enquiry ${result.reference} and would like to discuss my requirements.`)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105"
        >
          <WhatsAppIcon size={18} /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form data-testid="requirement-enquiry-form" onSubmit={submit} className="glass-card rounded-2xl p-6 sm:p-8" noValidate>
      {!compact && (
        <>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-50">Tell Us What Your Business Needs.</h3>
          <p className="mt-2 text-sm text-slate-400">
            Share your requirements, and our team will help you identify the right digital solution.
          </p>
        </>
      )}
      <input
        type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)}
        className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true"
      />
      <div className={`grid gap-4 sm:grid-cols-2 ${compact ? "mt-0" : "mt-6"}`}>
        <div>
          <label className={labelCls} htmlFor="full_name">Full Name *</label>
          <input id="full_name" data-testid="form-full-name-input" className={inputCls} value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)} placeholder="Your full name" />
          {errors.full_name && <p className="mt-1 text-xs text-red-400">{errors.full_name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="company_name">Company Name *</label>
          <input id="company_name" data-testid="form-company-input" className={inputCls} value={form.company_name}
            onChange={(e) => set("company_name", e.target.value)} placeholder="Your company" />
          {errors.company_name && <p className="mt-1 text-xs text-red-400">{errors.company_name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="mobile">Mobile Number *</label>
          <input id="mobile" data-testid="form-phone-input" className={inputCls} value={form.mobile}
            onChange={(e) => set("mobile", e.target.value)} placeholder="+91 ..." />
          {errors.mobile && <p className="mt-1 text-xs text-red-400">{errors.mobile}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="email">Business Email *</label>
          <input id="email" type="email" data-testid="form-email-input" className={inputCls} value={form.email}
            onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
          {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
        </div>
        {!compact && (
          <>
            <div>
              <label className={labelCls} htmlFor="city">City / Location</label>
              <input id="city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Coimbatore" />
            </div>
            <div>
              <label className={labelCls} htmlFor="industry">Industry</label>
              <select id="industry" data-testid="form-industry-select" className={inputCls} value={form.industry}
                onChange={(e) => set("industry", e.target.value)}>
                <option value="">Select your industry</option>
                {INDUSTRY_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <span className={labelCls}>Service Required *</span>
          <div data-testid="form-service-select" className="flex flex-wrap gap-2 mt-1">
            {SERVICE_OPTIONS.map((s) => (
              <button type="button" key={s} onClick={() => toggleService(s)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 ${
                  form.services.includes(s)
                    ? "border-blue-500 bg-blue-600/20 text-blue-300"
                    : "border-white/10 bg-slate-900/60 text-slate-400 hover:border-blue-500/50 hover:text-slate-200"
                }`}>
                {s}
              </button>
            ))}
          </div>
          {errors.services && <p className="mt-1 text-xs text-red-400">{errors.services}</p>}
        </div>
        {!compact && (
          <>
            <div>
              <label className={labelCls} htmlFor="website_url">Current Website URL</label>
              <input id="website_url" className={inputCls} value={form.website_url}
                onChange={(e) => set("website_url", e.target.value)} placeholder="https:// (optional)" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="budget">Estimated Budget</label>
                <select id="budget" className={inputCls} value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                  <option value="">Select</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="timeline">Project Timeline</label>
                <select id="timeline" className={inputCls} value={form.timeline} onChange={(e) => set("timeline", e.target.value)}>
                  <option value="">Select</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="requirements">Describe Your Requirements *</label>
          <textarea id="requirements" data-testid="form-requirements-input" rows={compact ? 3 : 4} className={inputCls}
            value={form.requirements} onChange={(e) => set("requirements", e.target.value)}
            placeholder="Tell us about your business goals, the services you need, and any current challenges." />
          {errors.requirements && <p className="mt-1 text-xs text-red-400">{errors.requirements}</p>}
        </div>
        <div className="sm:col-span-2">
          <span className={labelCls}>Preferred Contact Method</span>
          <div className="flex gap-2 mt-1">
            {["Phone", "WhatsApp", "Email"].map((m) => (
              <button type="button" key={m} onClick={() => set("preferred_contact", m)}
                className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-all ${
                  form.preferred_contact === m
                    ? "border-blue-500 bg-blue-600/20 text-blue-300"
                    : "border-white/10 bg-slate-900/60 text-slate-400 hover:text-slate-200"
                }`}>
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="form-consent-checkbox" checked={form.consent}
              onChange={(e) => set("consent", e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 accent-blue-600" />
            <span className="text-xs text-slate-400 leading-relaxed">
              I consent to RIDGELINE DIGI PARTNER collecting and using the information provided to respond to my
              enquiry, as described in the <Link to="/privacy" className="text-blue-400 hover:underline">Privacy Policy</Link>.
            </span>
          </label>
          {errors.consent && <p className="mt-1 text-xs text-red-400">{errors.consent}</p>}
        </div>
      </div>
      <button type="submit" data-testid="form-submit-btn" disabled={submitting}
        className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-500 hover:shadow-[0_0_40px_rgba(37,99,235,0.65)] disabled:opacity-60 disabled:cursor-not-allowed">
        {submitting && <Loader2 className="animate-spin" size={16} />}
        {submitting ? "Submitting..." : "Submit My Requirements"}
      </button>
    </form>
  );
}
